import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  X,
  ChevronDown,
  ChevronUp,
  Search,
  // Filter,
  Bell,
} from 'lucide-react'
import { Button, Text } from '@mantine/core'
import { useMarkNotificationRead } from '@/hooks/api/useNotifications'
import { NotificationSummary } from '@/types/api/notifications'
import { toast } from 'react-hot-toast'
import moment from 'moment'
import { NotificationsModalSkeleton } from '../ui/Skeleton'

interface NotificationsModalProps {
  notifications: NotificationSummary[]
  isLoading: boolean
  onClose: () => void
  onRefresh: () => void
}

interface GroupedNotification {
  data_id: string
  notifications: NotificationSummary[]
  hasUnread: boolean
  timestamp: string
}

const groupNotifications = (
  notifications: NotificationSummary[]
): GroupedNotification[] => {
  const grouped = notifications.reduce(
    (acc, notification) => {
      const groupKey = notification.related_entity_id || notification.id

      if (!acc[groupKey]) {
        acc[groupKey] = []
      }
      acc[groupKey].push(notification)
      return acc
    },
    {} as Record<string, NotificationSummary[]>
  )

  return Object.entries(grouped).map(([data_id, notifications]) => ({
    data_id,
    notifications,
    hasUnread: notifications.some(n => n.status === 'unread'),
    timestamp: notifications[0]?.created_at || '',
  }))
}

const NotificationsModal: React.FC<NotificationsModalProps> = ({
  notifications,
  isLoading,
  onClose,
  onRefresh,
}) => {
  const navigate = useNavigate()
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  )
  const [markingAsRead, setMarkingAsRead] = useState<Record<string, boolean>>(
    {}
  )
  const markAsReadMutation = useMarkNotificationRead()

  const groupedNotifications = groupNotifications(notifications)
  const [activeTab, setActiveTab] = useState<
    'team-feed' | 'my-work' | 'archive'
  >('team-feed')

  const formatCaseId = (case_id: string, createdAt: string) => {
    if (!case_id || !createdAt) return '—'

    const date = new Date(createdAt)
    const formattedDate = date.toISOString().slice(0, 10).replace(/-/g, '')
    const suffix = case_id.slice(-4).toUpperCase()

    return `C${formattedDate}-${suffix}`
  }

  const [search, setSearch] = useState('')

  const getPriorityConfig = (priority?: string) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return {
          label: 'High',
          dot: 'bg-[#FA5252]',
          border: 'border-[#FA5252]',
          bg: 'bg-[#FFF5F5]',
        }

      case 'medium':
        return {
          label: 'Medium',
          dot: 'bg-[#FF922B]',
          border: 'border-[#FF922B]',
          bg: 'bg-[#FFF4E6]',
        }

      default:
        return {
          label: 'Info',
          dot: 'bg-blue-500',
          border: 'border-blue-200',
          bg: 'bg-blue-50',
        }
    }
  }

  const filteredNotifications = groupedNotifications.filter(group =>
    JSON.stringify(group).toLowerCase().includes(search.toLowerCase())
  )
  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId],
    }))
  }

  const markGroupAsRead = async (group: GroupedNotification) => {
    if (!group.hasUnread) return

    setMarkingAsRead(prev => ({ ...prev, [group.data_id]: true }))

    try {
      const unreadNotifications = group.notifications.filter(
        n => n.status === 'unread'
      )

      await Promise.all(
        unreadNotifications.map(notification =>
          markAsReadMutation.mutateAsync({
            notificationId: notification.id,
            entity: 'dispatcher',
          })
        )
      )

      toast.success(
        `${unreadNotifications.length} notification${unreadNotifications.length > 1 ? 's' : ''} marked as read`
      )
      onRefresh()
    } catch (error) {
      console.error('Failed to mark notifications as read:', error)
      toast.error('Failed to mark notifications as read')
    } finally {
      setMarkingAsRead(prev => ({ ...prev, [group.data_id]: false }))
    }
  }

  const viewCaseDetails = (group: GroupedNotification) => {
    const caseId = group.data_id

    if (group.hasUnread) {
      markGroupAsRead(group)
    }

    if (caseId.startsWith('cas-')) {
      navigate(`/cases/${caseId}`)
    } else if (caseId.startsWith('ar-')) {
      navigate('/ambulance-request')
    } else {
      navigate('/cases')
    }
  }

  return (
    <div className="absolute right-0 top-full font-family-sans mt-2 z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 w-[600px] h-[75vh] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <Text
            fw={700}
            size="lg"
            className="font-family-heading text-[#413F3F]"
          >
            Notifications
          </Text>

          <Bell size={16} className="text-[#939090]" />
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-lg hover:bg-gray-100 cursor-pointer"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="px-4 pt-3">
        <div className="grid grid-cols-3 gap-2 bg-[#F5F5F7] rounded p-1">
          {[
            {
              key: 'team-feed',
              label: 'Team Feed',
            },
            {
              key: 'my-work',
              label: 'My Work',
            },
            {
              key: 'archive',
              label: 'Archive',
            },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as typeof activeTab)}
              className={`
              h-8 rounded-lg transition-all cursor-pointer
              ${
                activeTab === tab.key
                  ? 'bg-white shadow-sm text-primary font-medium border border-[#C9C6C5]'
                  : 'text-gray-600 hover:bg-blue-100/50'
              }
            `}
              style={{
                color: activeTab === tab.key ? '#254769' : '#2C2C2C',
                fontWeight: activeTab === tab.key ? 500 : 400,
                fontSize: '14px',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 py-2.5">
        <div className="flex gap-3">
          <div className="flex-1 border border-[#939090] rounded-lg px-4 h-10 flex items-center gap-2">
            <Search size={16} className="text-gray-400 -mt-px" />

            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by case and actions..."
              className="flex-1 outline-none bg-transparent"
              style={{ fontSize: '14px' }}
            />
          </div>

          {/* <button className="h-12 px-4 border rounded-lg flex items-center gap-2">
            <Filter size={18} />
            Filter by
          </button> */}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto pl-4 pr-2.5 pb-4">
        {isLoading ? (
          <NotificationsModalSkeleton />
        ) : activeTab === 'archive' ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="-mt-18 w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <Text size="xl">📁</Text>
            </div>

            <Text fw={600} size="sm">
              No Archived Items
            </Text>

            <Text size="xs" c="dimmed">
              Resolved cases and cleared notifications will appear here.
            </Text>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <Text c="dimmed">No notifications found.</Text>
          </div>
        ) : (
          filteredNotifications.map(group => {
            const latest = group.notifications[0]
            const priority = getPriorityConfig(latest?.priority)
            const isExpanded = expandedGroups[group.data_id]
            const isMarkingRead = markingAsRead[group.data_id]

            return (
              <div
                key={group.data_id}
                className={`mb-4 border border-[#C6C5D0] rounded overflow-hidden ${isExpanded ? priority.border : 'border-gray-200'}`}
              >
                <div className="px-3 py-2">
                  {/* Header */}
                  <div className="flex justify-between items-start">
                    <div>
                      <Text fw={600} size="xs" c="#23476B">
                        {formatCaseId(group.data_id, group.timestamp)}
                      </Text>

                      <Text size="xs" c="dimmed" mt={2.5}>
                        {new Date(group.timestamp).toLocaleString()}
                      </Text>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <div className={`w-2 h-2 rounded-full ${priority.dot}`} />
                      <Text fw={400} size="xs">
                        {priority.label}
                      </Text>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="mt-2.5">
                    <Text fw={600} size="sm">
                      {latest?.title}
                    </Text>
                    <Text size="xs" className="line-clamp-2 text-[#2E2E2E]">
                      {latest?.message}
                    </Text>
                  </div>

                  {/* Activity Bar */}
                  <div
                    onClick={() => toggleGroupExpansion(group.data_id)}
                    className={`mt-2 px-4 py-2 rounded cursor-pointer border ${priority.bg} ${priority.border}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Text size="xs" c="dimmed">
                          Updated By
                        </Text>

                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full bg-[#24476B] text-white text-xs flex items-center justify-center">
                            {(latest.updated_by_name || 'S')[0]}
                          </div>
                          <Text fw={500} size="xs">
                            {latest.updated_by_name || 'System'}
                          </Text>
                        </div>

                        <Text size="xs" c="dimmed">
                          {moment(new Date(latest.created_at)).format('h:mm A')}
                        </Text>
                      </div>

                      {isExpanded ? (
                        <ChevronUp size={16} />
                      ) : (
                        <ChevronDown size={16} />
                      )}
                    </div>

                    {/* Timeline */}
                    {isExpanded && (
                      <div className="mt-4 pl-4">
                        <div className="border-l-2 border-gray-200 ml-2">
                          {group.notifications.map(notification => (
                            <div
                              key={notification.id}
                              className="relative pb-4 pl-6"
                            >
                              <div className="absolute -left-[7px] top-1 w-3 h-3 rounded-full border-2 border-primary bg-white" />

                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  {/* <Text fw={600} size="sm">
                                  System
                                </Text> */}

                                  <Text size="xs" fw={500}>
                                    {notification.title}
                                  </Text>

                                  <Text size="xs" c="dimmed">
                                    {moment(
                                      new Date(notification.created_at)
                                    ).format('MMM D YYYY - h:mm A')}
                                  </Text>
                                </div>

                                <Text size="xs" c="#5D5E67">
                                  {notification.message}
                                </Text>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3 mt-5">
                    <Button
                      variant="subtle"
                      color="gray"
                      size="compact-md"
                      style={{ fontSize: '12px' }}
                      disabled={!group.hasUnread || isMarkingRead}
                      onClick={() => markGroupAsRead(group)}
                    >
                      Archive
                    </Button>

                    <Button
                      variant="light"
                      color="gray"
                      size="compact-md"
                      style={{
                        background: '#E9F1FF',
                        fontSize: '12px',
                        borderRadius: '5px',
                      }}
                      disabled
                    >
                      Defer
                    </Button>

                    <Button
                      onClick={() => viewCaseDetails(group)}
                      size="compact-md"
                      styles={{
                        root: {
                          background: '#000',
                          color: '#fff',
                          fontSize: '12px',
                          borderRadius: '5px',
                        },
                      }}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default NotificationsModal
